import { ZoningType } from '@popety-io/types'

/**
 * Translations interface for ZoningSection component
 */
export interface ZoningSectionTranslations {
  title?: string
  sectionDescription?: string
  underExploited?: string
  loading?: string
  error?: string
  noData?: string
  noZonesForType?: string
  unknownZone?: string
  zoneTypes?: Partial<Record<ZoningType, string>>
  zonePriorityLabels?: Partial<Record<ZoningType, string>>
  zoneDescriptions?: Partial<Record<ZoningType, string>>
  metricsTable?: {
    title?: string
    resetToDefault?: string
    editAuthorizedValues?: string
    saveChanges?: string
    cancel?: string
    confirmReset?: string
    determiningLandArea?: string
    noneOption?: string
    headers?: {
      indices?: string
      current?: string
      authorized?: string
      difference?: string
    }
    groups?: {
      heights?: string
      floors?: string
      setbacks?: string
      usageRatios?: string
      environmental?: string
      other?: string
    }
    derivedSurfaces?: {
      determiningBuiltArea?: string
      determiningBuiltAreaDesc?: string
      builtArea?: string
      builtAreaDesc?: string
      determiningFloorArea?: string
      determiningFloorAreaDesc?: string
      grossUsefulFloorArea?: string
      grossUsefulFloorAreaDesc?: string
      floorArea?: string
      floorAreaDesc?: string
      builtVolume?: string
      builtVolumeDesc?: string
    }
    metricLabels?: {
      cos?: string
      cus?: string
      ibus?: string
      ios?: string
      ius?: string
      im?: string
      vbr?: string
      altitude?: string
      roofHeight?: string
      totalHeight?: string
      heightWithoutRoof?: string
      totalFloors?: string
      mainFloors?: string
      atticFloors?: string
      undergroundFloors?: string
      commercialRatio?: string
      livingRatio?: string
      officeRatio?: string
      greenRatio?: string
      noiseSensitivityLevel?: string
      distanceLimit?: string
      numberOfUnits?: string
      constructionArrangement?: string
      maxLength?: string
    }
    progressBar?: {
      currentSituation?: string
      used?: string
      authorized?: string
      utilization?: string
      remainingPotential?: string
      available?: string
    }
    /** Regelbauweise envelope block — see ZoneMetricsTableTranslations */
    envelope?: import('./components/ZoneMetricsTable').ZoneMetricsTableTranslations['envelope']
    /** Exclusive concept labels — see ZoneMetricsTableTranslations */
    concepts?: import('./components/ZoneMetricsTable').ZoneMetricsTableTranslations['concepts']
    /** Metric visibility controls — see ZoneMetricsTableTranslations */
    visibility?: import('./components/ZoneMetricsTable').ZoneMetricsTableTranslations['visibility']
  }
  sourceLabels?: {
    manual?: string
    geoportal?: string
    llm?: string
    estimate?: string
    old?: string
  }
  status?: {
    current?: string
    future?: string
    past?: string
  }
  header?: {
    unknownZone?: string
    buildingArea?: string
    nonBuildingArea?: string
    adopted?: string
    abrogated?: string
    purpose?: string
    constructionArrangement?: string
    constructionArrangementValues?: {
      C?: string
      NC?: string
      'C / NC'?: string
    }
    references?: string
    documents?: string
    /** Suffix shown after the percentage in the plot-coverage pill (e.g., "of plot") */
    plotCoverage?: string
  }
  documents?: {
    title?: string
    loading?: string
    noDocuments?: string
    view?: string
    modalTitle?: string
    searchPlaceholder?: string
    searchAriaLabel?: string
    clearSearchAriaLabel?: string
    noDocumentSelected?: string
    selectDocumentHint?: string
    important?: string
    noPreviewAvailable?: string
    noPreviewHint?: string
    documentTypes?: {
      rule?: string
      map?: string
    }
    sourceLabels?: {
      lupa?: string
      lupc?: string
      lupo?: string
      lup?: string
      region?: string
      country?: string
    }
    sourceTitles?: {
      lupa?: string
      lupc?: string
      lupo?: string
      lup?: string
      region?: string
      country?: string
    }
  }
  legend?: {
    title?: string
    sources?: {
      title?: string
      geoportal?: string
      regulation?: string
      algorithm?: string
      manual?: string
      userEdit?: string
    }
    agreement?: {
      title?: string
      identical?: string
      conflict?: string
      unique?: string
      conflictNote?: string
    }
    difference?: {
      title?: string
      remaining?: string
      exceeded?: string
    }
    authorizedValues?: {
      title?: string
      clickForDetails?: string
      notAvailable?: string
    }
  }
}

/**
 * Default English translations for ZoningSection
 */
export const DEFAULT_ZONING_TRANSLATIONS: ZoningSectionTranslations = {
  title: 'Zoning',
  sectionDescription: 'Zoning regulations and development potential for this land parcel',
  underExploited: 'Under-Exploited',
  loading: 'Loading zoning data...',
  error: 'Failed to load zoning data',
  noData: 'No zoning data available for this land',
  noZonesForType: 'No zones found for this type',
  unknownZone: 'Unknown zone',
  zoneTypes: {
    cantonal: 'Cantonal',
    local: 'Local',
    partial: 'Partial',
    general: 'Communal',
  },
  zonePriorityLabels: {
    cantonal: 'Cantonal Authority - Overrides All',
    local: 'Detailed Planning - Neighborhood Rules',
    partial: 'Special Zones - Additional Restrictions',
    general: 'Base Zoning - Fundamental Land Use',
  },
  zoneDescriptions: {
    cantonal:
      'Cantonal zoning regulations (PAC, Kantonaler Nutzungsplan) set by regional authorities. These rules take absolute precedence over all municipal plans and define overarching development constraints such as protected areas, regional infrastructure corridors, and cantonal interests.',
    local:
      'Detailed neighborhood development plans (PQ, PLQ, Quartierplan, Gestaltungsplan) that specify precise construction requirements including building placement, dimensions, access, and design guidelines for specific districts within the municipality.',
    partial:
      'Special overlay zones (PPA, Sondernutzungsplan, IBUS zones) that apply additional restrictions or modifications on top of base zoning. These can include heritage protection, modified density ratios, environmental constraints, or specific development requirements.',
    general:
      'Municipal base zoning (PGA, PAL, BZO, Nutzungszonenplan) that establishes the fundamental land use category for this parcel, defining whether it is designated for residential, commercial, industrial, agricultural use, or is a non-building zone.',
  },
  metricsTable: {
    title: 'Zoning Metrics',
    resetToDefault: 'Reset to Default',
    editAuthorizedValues: 'Edit Authorized Values',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    confirmReset:
      'Are you sure you want to reset all values to defaults? This action cannot be undone.',
    determiningLandArea: 'Determining land area (DLA)',
    noneOption: 'None (hide this metric)',
    headers: {
      indices: 'Indices',
      current: 'Current',
      authorized: 'Authorized',
      difference: 'Difference',
    },
    groups: {
      heights: 'HEIGHTS',
      floors: 'FLOORS',
      setbacks: 'SETBACKS & DIMENSIONS',
      usageRatios: 'USAGE RATIOS',
      environmental: 'ENVIRONMENTAL',
      other: 'OTHER',
    },
    derivedSurfaces: {
      determiningBuiltArea: 'Determining built area (DBA)',
      determiningBuiltAreaDesc: 'Determining built area',
      builtArea: 'Built area (BA)',
      builtAreaDesc: 'Built area',
      determiningFloorArea: 'Determining floor area (DFA)',
      determiningFloorAreaDesc: 'Determining floor area',
      grossUsefulFloorArea: 'Gross usable floor area (GUFA)',
      grossUsefulFloorAreaDesc: 'Gross usable floor area',
      floorArea: 'Floor area (FA)',
      floorAreaDesc: 'Floor area',
      builtVolume: 'Reference building volume (RBV)',
      builtVolumeDesc: 'Reference building volume',
    },
    metricLabels: {
      cos: 'BCC',
      cus: 'FAC',
      ibus: 'GFAR',
      ios: 'BCR',
      ius: 'FAR',
      im: 'BVR',
      vbr: 'RBV',
      altitude: 'Altitude',
      roofHeight: 'Roof Height',
      totalHeight: 'Total Height',
      heightWithoutRoof: 'Height w/o Roof',
      totalFloors: 'Total Floors',
      mainFloors: 'Main Floors',
      atticFloors: 'Attic Floors',
      undergroundFloors: 'Underground Floors',
      commercialRatio: 'Commercial Ratio',
      livingRatio: 'Living Ratio',
      officeRatio: 'Office Ratio',
      greenRatio: 'Green Ratio',
      noiseSensitivityLevel: 'Noise Sensitivity Level',
      distanceLimit: 'Distance Limit',
      numberOfUnits: 'Number of Units',
      constructionArrangement: 'Construction Arrangement',
      maxLength: 'Max Length',
    },
    progressBar: {
      currentSituation: 'Current situation',
      used: 'Used',
      authorized: 'Authorized',
      utilization: 'Utilization',
      remainingPotential: 'Remaining Potential',
      available: 'Available',
    },
  },
  sourceLabels: {
    manual: 'Manual',
    geoportal: 'Geoportal',
    llm: 'AI Generated',
    estimate: 'Estimated',
    old: 'Legacy',
  },
  status: {
    current: 'Current',
    future: 'Future',
    past: 'Past',
  },
  header: {
    unknownZone: 'Unknown zone',
    buildingArea: 'Building Area',
    nonBuildingArea: 'Non-Building Area',
    adopted: 'Adopted',
    abrogated: 'Abrogated',
    purpose: 'Purpose',
    constructionArrangement: 'Construction Arrangement',
    constructionArrangementValues: {
      C: 'Contiguous',
      NC: 'Non-contiguous',
      'C / NC': 'Contiguous / Non-contiguous',
    },
    references: 'References',
    documents: 'Documents',
    plotCoverage: 'of plot',
  },
  documents: {
    title: 'Documents',
    loading: 'Loading documents...',
    noDocuments: 'No documents available',
    view: 'View',
    modalTitle: 'Zone Documents',
    searchPlaceholder: 'Search documents...',
    searchAriaLabel: 'Search documents',
    clearSearchAriaLabel: 'Clear search',
    noDocumentSelected: 'No Document Selected',
    selectDocumentHint: 'Select a document from the list to view its contents',
    important: 'Important',
    noPreviewAvailable: 'No Preview Available',
    noPreviewHint: 'This document does not have a PDF preview URL available',
    documentTypes: {
      rule: 'Rule',
      map: 'Map',
    },
    sourceLabels: {
      lupa: 'Local Plan',
      lupc: 'Cantonal Plan',
      lupo: 'Overlay Plan',
      lup: 'Communal Plan',
      region: 'Cantonal',
      country: 'Federal',
    },
    sourceTitles: {
      lupa: 'Local Plan Documents',
      lupc: 'Cantonal Plan Documents',
      lupo: 'Overlay Plan Documents',
      lup: 'Communal Plan Documents',
      region: 'Cantonal Documents',
      country: 'Federal Documents (Switzerland)',
    },
  },
  legend: {
    title: 'Legend',
    sources: {
      title: 'Data Sources',
      geoportal: 'Official geoportal',
      regulation: 'Extracted from regulation',
      algorithm: 'Algorithmic estimate',
      manual: 'Manually replaced',
      userEdit: 'Replaced by user',
    },
    agreement: {
      title: 'Source Agreement',
      identical: 'Identical sources',
      conflict: 'Sources in conflict',
      unique: 'Unique source',
      conflictNote: '(strikethrough = non-retained value)',
    },
    difference: {
      title: 'Difference',
      remaining: 'Remaining potential',
      exceeded: 'Exceeded',
    },
    authorizedValues: {
      title: 'Authorized Values',
      clickForDetails: 'Click for details',
      notAvailable: 'Not available',
    },
  },
}
